import {
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import GKModal from "components/GKModal";
import GKTextArea from "components/GKTextarea";
import GKTextField from "components/GKTextField";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import axiosInstance from "utils/axiosInstance";

const TemplateModal = ({ open, setOpen, refetch, templateData }) => {
  const templateId = templateData?.id;

  const { mutate: handleCreateTemplate } = useMutation({
    mutationKey: ["CREATE_TEMPLATE"],
    mutationFn: (data) =>
      axiosInstance.post("/super_admin_whatsapp/create_template/", data),
  });

  const { mutate: handleUpdateTemplate } = useMutation({
    mutationKey: ["UPDATE_TEMPLATE"],
    mutationFn: (data) =>
      axiosInstance.patch(
        `/super_admin_whatsapp/${templateId}/update_template/`,
        data
      ),
    onSuccess: (response) => {
      refetch();
      setOpen(false);
    },
    onError: (error) => {
      console.error("Update failed:", error);
    },
  });

  const { mutate: handleDeleteTemplate } = useMutation({
    mutationKey: ["DELETE_TEMPLATE"],
    mutationFn: () => {
      console.log("Attempting to delete template with ID:", templateId);
      return axiosInstance.delete(
        `/super_admin_whatsapp/${templateId}/delete_template/`
      );
    },
    onSuccess: (response) => {
      console.log("Delete successful:", response);
      refetch();
      setOpen(false);
    },
    onError: (error) => {
      console.error("Delete failed:", error);
    },
  });

  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      templateTitle: "",
      templateContent: "",
      includeDocument: false,
      templateName: "",
      approvedStatus: false,
    },
  });

  useEffect(() => {
    if (templateId) {
      setValue("templateTitle", templateData?.templateTitle || "");
      setValue("templateContent", templateData?.templateContent || "");
      setValue("includeDocument", templateData?.includeDocument || false);
      setValue("templateName", templateData?.templateName || "");
      setValue("approvedStatus", templateData?.approvedStatus || false);
    }
  }, [templateId, templateData, setValue]);

  const onSubmit = (values) => {
    if (templateId) {
      handleUpdateTemplate(values);
    } else {
      handleCreateTemplate(values);
    }
  };

  const dynamicMessage = `Note: For dynamic value please type {{#}}. Eg. {{advisor_name}}, {{advisor_phone}}, {{advisor_email}}`;

  const insertVariable = (variable) => {
    const currentContent = watch("templateContent");
    setValue("templateContent", `${currentContent}{{${variable}}}`);
  };

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      modalTitle={templateId ? "Update Template" : "Create Template"}
      footer={
        <Grid container justifyContent={"flex-end"} gap={1}>
          <Button
            sx={{
              fontSize: "0.7rem",
              fontFamily: '"Arial", sans-serif',
              textTransform: "none",
            }}
            type="submit"
            form="create-template-form"
            variant="outlined"
          >
            {templateId ? "Update" : "Save"}
          </Button>
        </Grid>
      }
      titleComponent={
        templateId && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleDeleteTemplate()}
          >
            Delete Template
          </Button>
        )
      }
    >
      <form id="create-template-form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="templateName"
                control={control}
                render={({ field }) => (
                  <GKTextField
                    inputLabel="Name"
                    {...field}
                    disabled={templateId}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="templateTitle"
                control={control}
                render={({ field }) => (
                  <GKTextField
                    inputLabel="Purpose"
                    {...field}
                    disabled={templateData?.approvedStatus}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} mt={1}>
            <Controller
              name="templateContent"
              control={control}
              render={({ field }) => (
                <GKTextArea
                  minRows={4}
                  aria-label="maximum height"
                  inputLabel="Content"
                  placeholder="Content"
                  {...field}
                  disabled={templateData?.approvedStatus}
                  style={{ width: "97%" }}
                />
              )}
            />
            <Typography fontSize={12}>{dynamicMessage}</Typography>
          </Grid>
          <Grid item xs={12} display="flex" alignItems="center" gap={1}>
            <Button
              sx={{
                fontSize: "0.7rem",
                fontFamily: '"Arial", sans-serif',
                textTransform: "none",
              }}
              variant="outlined"
              onClick={() => insertVariable("advisor_name")}
            >
              Insert Advisor Name
            </Button>
            <Button
              sx={{
                fontSize: "0.7rem",
                fontFamily: '"Arial", sans-serif',
                textTransform: "none",
              }}
              variant="outlined"
              onClick={() => insertVariable("advisor_phone")}
            >
              Insert Advisor Phone
            </Button>
            <Button
              sx={{
                fontSize: "0.7rem",
                fontFamily: '"Arial", sans-serif',
                textTransform: "none",
              }}
              variant="outlined"
              onClick={() => insertVariable("advisor_email")}
            >
              Insert Advisor Email
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="includeDocument"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  style={{
                    marginLeft: 2,
                  }}
                  control={
                    <Switch
                      {...field}
                      checked={field.value}
                      disabled={templateData?.approvedStatus}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  labelPlacement="end"
                  label={
                    <b
                      style={{
                        fontSize: 12,
                      }}
                    >
                      Include Document
                    </b>
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="approvedStatus"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  style={{
                    marginLeft: 2,
                  }}
                  control={
                    <Switch
                      {...field}
                      checked={field.value}
                      disabled={templateData?.approvedStatus}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  labelPlacement="end"
                  label={
                    <b
                      style={{
                        fontSize: 12,
                      }}
                    >
                      Approved Status
                    </b>
                  }
                />
              )}
            />
          </Grid>
        </Grid>
      </form>
    </GKModal>
  );
};

export default TemplateModal;
